import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { walletHttp } from 'shared/api/wallet-axios';
const PAGE_SIZE = 20;
function normalizeCustomerRow(raw) {
    if (raw == null || typeof raw.id !== 'number') {
        return null;
    }
    const nid = raw.NID ?? raw.tazkiraNumber ?? null;
    return {
        id: raw.id,
        name: raw.name,
        fatherName: raw.fatherName,
        mobile: raw.mobile,
        customerReference: raw.customerReference,
        fundingReference: raw.fundingReference,
        tazkiraNumber: nid ?? raw.customerReference ?? null,
    };
}
function normalizePartnerList(payload) {
    if (Array.isArray(payload)) {
        return payload
            .map(row => normalizeCustomerRow(row))
            .filter((p) => p != null);
    }
    const content = payload?.content;
    if (!Array.isArray(content)) {
        return [];
    }
    return content
        .map(row => normalizeCustomerRow(row))
        .filter((p) => p != null);
}
function pageMeta(payload) {
    if (payload && typeof payload === 'object' && 'last' in payload) {
        return { last: !!payload.last };
    }
    return { last: true };
}
function mergeByIdSortedDesc(existing, chunk) {
    const m = new Map();
    for (const r of existing) {
        m.set(r.id, r);
    }
    for (const r of chunk) {
        m.set(r.id, r);
    }
    return Array.from(m.values()).sort((a, b) => b.id - a.id);
}
function buildCustomersUrl(page, nameFilter) {
    const p = new URLSearchParams();
    p.set('sort', 'id,desc');
    p.set('size', String(PAGE_SIZE));
    p.set('page', String(page));
    const f = nameFilter.trim();
    if (f) {
        p.set('name.contains', f);
    }
    return `api/customers?${p.toString()}&cacheBuster=${Date.now()}`;
}
function lazyLastIndex(e) {
    const last = e.last;
    return typeof last === 'number' ? last : 0;
}
/**
 * Paginated wallet customers for card dialogs: server-side search via filter box,
 * lazy loading further pages while scrolling.
 */
export function usePartnerCustomersLazy(bootstrap) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const filterRef = useRef('');
    const nextPageRef = useRef(0);
    const hasMoreRef = useRef(true);
    const inFlightRef = useRef(false);
    const seqRef = useRef(0);
    const filterDebounceRef = useRef(null);
    const runFetch = useCallback(async (page, nameFilter, mode, requestSeq) => {
        const url = buildCustomersUrl(page, nameFilter);
        try {
            const res = await walletHttp().get(url);
            if (seqRef.current !== requestSeq) {
                return;
            }
            const list = normalizePartnerList(res.data);
            const { last } = pageMeta(res.data);
            const chunkEmpty = list.length === 0;
            hasMoreRef.current = !last && !chunkEmpty;
            nextPageRef.current = page + 1;
            setRows(prev => (mode === 'replace' ? mergeByIdSortedDesc([], list) : mergeByIdSortedDesc(prev, list)));
        }
        catch {
            if (seqRef.current !== requestSeq) {
                return;
            }
            if (mode === 'replace') {
                setRows([]);
            }
            hasMoreRef.current = false;
        }
    }, []);
    const startNewSearch = useCallback((nameFilter) => {
        filterRef.current = nameFilter;
        seqRef.current += 1;
        const mySeq = seqRef.current;
        nextPageRef.current = 0;
        hasMoreRef.current = true;
        setLoading(true);
        setLoadingMore(false);
        void (async () => {
            await runFetch(0, nameFilter, 'replace', mySeq);
            if (seqRef.current === mySeq) {
                setLoading(false);
            }
        })();
    }, [runFetch]);
    useEffect(() => {
        startNewSearch('');
        return () => {
            if (filterDebounceRef.current) {
                clearTimeout(filterDebounceRef.current);
            }
        };
    }, [startNewSearch]);
    useEffect(() => {
        if (!bootstrap) {
            return;
        }
        let cancelled = false;
        const cref = bootstrap.customerReference?.trim();
        const fref = bootstrap.fundingReference?.trim();
        const extras = [];
        if (cref) {
            extras.push({ 'customerReference.contains': cref });
        }
        if (fref && fref !== cref) {
            extras.push({ 'fundingReference.contains': fref });
        }
        void (async () => {
            for (const pair of extras) {
                if (cancelled) {
                    return;
                }
                const p = new URLSearchParams();
                p.set('sort', 'id,desc');
                p.set('size', '10');
                p.set('page', '0');
                for (const [k, v] of Object.entries(pair)) {
                    p.set(k, v);
                }
                try {
                    const res = await walletHttp().get(`api/customers?${p.toString()}&cacheBuster=${Date.now()}`);
                    if (cancelled) {
                        return;
                    }
                    const list = normalizePartnerList(res.data);
                    if (list.length > 0) {
                        setRows(prev => mergeByIdSortedDesc(prev, list));
                    }
                }
                catch {
                    /* non-fatal */
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [bootstrap?.customerReference, bootstrap?.fundingReference]);
    const loadNextPage = useCallback(async () => {
        if (!hasMoreRef.current || inFlightRef.current) {
            return;
        }
        const nameFilter = filterRef.current;
        const page = nextPageRef.current;
        inFlightRef.current = true;
        const mySeq = seqRef.current;
        setLoadingMore(true);
        try {
            await runFetch(page, nameFilter, 'append', mySeq);
        }
        finally {
            inFlightRef.current = false;
            if (seqRef.current === mySeq) {
                setLoadingMore(false);
            }
        }
    }, [runFetch]);
    const onFilter = useCallback((event) => {
        const next = event.filter ?? '';
        if (filterDebounceRef.current) {
            clearTimeout(filterDebounceRef.current);
        }
        filterDebounceRef.current = setTimeout(() => {
            startNewSearch(next);
        }, 350);
    }, [startNewSearch]);
    const onLazyLoad = useCallback((event) => {
        const end = lazyLastIndex(event);
        if (end < rows.length - 5) {
            return;
        }
        void loadNextPage();
    }, [loadNextPage, rows.length]);
    const virtualScrollerOptions = useMemo(() => ({
        itemSize: 38,
        lazy: true,
        showLoader: true,
        loading: loadingMore,
        step: PAGE_SIZE,
        onLazyLoad,
    }), [loadingMore, onLazyLoad]);
    return {
        rows,
        loading,
        loadingMore,
        onFilter,
        virtualScrollerOptions,
    };
}
